import { authFetch } from '@/lib/auth-fetch';
import { getApiUrl } from '@/lib/api-config';
import type {
  AccountingConnection,
  AccountingAsset,
  ImportResult,
  FilterOptions,
} from '@/types/asset-import.types';
import type { AgentSessionStatus } from '@/types/agent.types';

export interface FinancialsAssetCategory {
  code: string;
  name: string | null;
}

export interface FinancialsAssetPreview {
  id: string;
  primaryKey: number;
  assetCode: string;
  description: string | null;
  category: string | null;
  purchasePrice: number;
  purchaseDate: string | null;
  supplier: string | null;
  serialNumber: string | null;
  location: string | null;
  status: string;
  notes: string | null;
}

export interface ImportAssetOverride {
  primaryKey: number;
  assetCode?: string;
  description?: string;
  category?: string;
}

export interface ImportFromFinancialsResult {
  importedCount: number;
  skippedCount: number;
  errorCount: number;
  message: string;
  errors: string[];
  skippedAssetCodes: string[];
}

export interface FetchAssetsWithAgentResult {
  assets: AccountingAsset[];
  agentSessionId?: string;
  agentStatus?: AgentSessionStatus;
}

// Helper function to map asset status from API response
function mapAssetStatus(status: string): AccountingAsset['status'] {
  if (status === 'already-imported') {
    return 'already-imported';
  }
  if (status === 'excluded') {
    return 'excluded';
  }
  return 'new';
}

class AccountingImportService {
  private readonly baseUrl = '/api/assets';

  private getApiBaseUrl(): string {
    const apiUrlFromConfig = getApiUrl();
    return apiUrlFromConfig ?? '';
  }

  async getAccountingConnection(financialsInstanceId: string): Promise<AccountingConnection> {
    return {
      id: financialsInstanceId,
      systemName: 'Access Financials',
      systemType: 'access-financials',
      isConnected: true,
      lastSyncDate: new Date().toISOString(),
      instanceId: financialsInstanceId,
    };
  }

  async fetchAccountingAssets(
    financialsInstanceId: string,
    filters: FilterOptions,
    enableAgentProcessing: boolean = false
  ): Promise<FetchAssetsWithAgentResult> {
    const params = new URLSearchParams();
    params.append('financialsInstanceId', financialsInstanceId);

    if (filters.dateFrom) {
      const dateFrom = new Date(filters.dateFrom);
      params.append('dateFrom', dateFrom.toISOString().split('T')[0]);
    }

    if (filters.dateTo) {
      const dateTo = new Date(filters.dateTo);
      params.append('dateTo', dateTo.toISOString().split('T')[0]);
    }

    if (filters.category && filters.category !== 'all') {
      params.append('categoryCode', filters.category);
    }

    if (enableAgentProcessing) {
      params.append('enableAgentProcessing', 'true');
    }

    const response = await authFetch(`${this.getApiBaseUrl()}${this.baseUrl}/financials/fetch?${params.toString()}`);

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to fetch assets' }));
      throw new Error(error.message ?? 'Failed to fetch assets from Financials');
    }

    const data = await response.json();

    // Handle both old format (array) and new format (object with assets array)
    const assetsArray: FinancialsAssetPreview[] = Array.isArray(data) ? data : (data.assets ?? []);

    const assets: AccountingAsset[] = assetsArray.map((asset) => ({
      id: asset.id,
      assetCode: asset.assetCode,
      description: asset.description ?? '',
      category: asset.category ?? 'Uncategorized',
      financialsCategory: asset.category ?? undefined,
      purchasePrice: asset.purchasePrice,
      purchaseDate: asset.purchaseDate ?? '',
      supplier: asset.supplier ?? '',
      serialNumber: asset.serialNumber ?? undefined,
      location: asset.location ?? '',
      status: mapAssetStatus(asset.status),
      notes: asset.notes ?? undefined,
      primaryKey: asset.primaryKey,
    }));

    return {
      assets,
      agentSessionId: data.agentSessionId,
      agentStatus: data.agentStatus,
    };
  }

  async importAccountingAssets(
    financialsInstanceId: string,
    selectedAssets: AccountingAsset[]
  ): Promise<ImportResult> {
    // Use primaryKey if available, otherwise fall back to parsing id
    const assetPrimaryKeys = selectedAssets.map((asset) => asset.primaryKey ?? parseInt(asset.id, 10));

    const assetOverrides: ImportAssetOverride[] = selectedAssets.map((asset) => ({
      primaryKey: asset.primaryKey ?? parseInt(asset.id, 10),
      assetCode: asset.assetCode,
      description: asset.description,
      category: asset.category,
    }));

    const response = await authFetch(
      `${this.getApiBaseUrl()}${this.baseUrl}/financials/import-selected?financialsInstanceId=${encodeURIComponent(financialsInstanceId)}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ assetPrimaryKeys, assetOverrides }),
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to import assets' }));
      throw new Error(error.message ?? 'Failed to import assets from Financials');
    }

    const result: ImportFromFinancialsResult = await response.json();

    return {
      success: result.errorCount === 0,
      importedCount: result.importedCount,
      failedCount: result.errorCount,
      skippedCount: result.skippedCount,
      message: result.message,
    };
  }

  async getFinancialsCategories(financialsInstanceId: string): Promise<FinancialsAssetCategory[]> {
    const response = await authFetch(
      `${this.getApiBaseUrl()}${this.baseUrl}/financials/categories?financialsInstanceId=${encodeURIComponent(financialsInstanceId)}`
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to fetch categories' }));
      throw new Error(error.message ?? 'Failed to fetch categories from Financials');
    }

    return response.json();
  }
}

export const accountingImportService = new AccountingImportService();
